/**
 * Keeps short-lived Box OAuth state in the current browser session only.
 *
 * Human OAuth must not become a durable workspace secret or survive across
 * browser sessions. Age, scope, Vault, and user checks bind cached tokens and
 * pending state to the consent flow that created them.
 */
import type { BoxSessionToken } from '../types'

const TOKEN_PREFIX = 'vault.box.token'
const PENDING_KEY = 'vault.box.oauth.pending'
const MAX_TOKEN_AGE_MS = 60 * 60 * 1000
const MAX_PENDING_AGE_MS = 10 * 60 * 1000
const BOX_OAUTH_SCOPE = 'root_readwrite'

interface StoredToken {
  accessToken: string
  expiresAt: number
}

interface PendingOAuth {
  state: string
  orgId: string
  vaultId: string
  feedId?: string
  redirectUri: string
  returnHash: string
  createdAt: number
  callbackSearch?: string
}

export interface BoxOAuthCallback extends PendingOAuth {
  code: string
}

const tokenKey = (orgId: string, vaultId: string) =>
  `${TOKEN_PREFIX}.${encodeURIComponent(orgId)}.${encodeURIComponent(vaultId)}`

const parse = <T>(value: string | null): T | null => {
  if (!value) return null
  try {
    return JSON.parse(value) as T
  } catch {
    return null
  }
}

const isConnectorHash = (hash: unknown): hash is string => (
  typeof hash === 'string'
  && (hash === '#/workspace-connectors' || hash.startsWith('#/workspace-connectors?'))
)

export const getBoxToken = (
  orgId: string,
  vaultId: string,
  now = Date.now(),
): string | null => {
  const key = tokenKey(orgId, vaultId)
  const token = parse<StoredToken>(sessionStorage.getItem(key))
  if (!token || !token.accessToken || token.expiresAt <= now) {
    sessionStorage.removeItem(key)
    return null
  }
  return token.accessToken
}

export const storeBoxToken = (
  orgId: string,
  vaultId: string,
  token: BoxSessionToken,
  now = Date.now(),
) => {
  const expiresInMs = Math.min(token.expiresIn * 1000, MAX_TOKEN_AGE_MS)
  sessionStorage.setItem(tokenKey(orgId, vaultId), JSON.stringify({
    accessToken: token.accessToken,
    expiresAt: now + expiresInMs,
  } satisfies StoredToken))
}

export const clearBoxToken = (orgId: string, vaultId: string) => {
  sessionStorage.removeItem(tokenKey(orgId, vaultId))
}

export const beginBoxOAuth = (
  clientId: string,
  orgId: string,
  vaultId: string,
  feedId?: string,
) => {
  const returnHash = window.location.hash
  if (!isConnectorHash(returnHash)) {
    throw new Error('Box authorization must start from Connectors')
  }
  const state = crypto.randomUUID()
  const redirectUri = `${window.location.origin}${window.location.pathname}`
  const pending: PendingOAuth = {
    state,
    orgId,
    vaultId,
    feedId,
    redirectUri,
    returnHash,
    createdAt: Date.now(),
  }
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending))
  const authorization = new URL('https://account.box.com/api/oauth2/authorize')
  authorization.searchParams.set('response_type', 'code')
  authorization.searchParams.set('client_id', clientId)
  authorization.searchParams.set('redirect_uri', redirectUri)
  authorization.searchParams.set('scope', BOX_OAUTH_SCOPE)
  authorization.searchParams.set('state', state)
  window.location.assign(authorization)
}

export const restoreBoxOAuthCallbackRoute = (
  search = window.location.search,
): boolean => {
  const params = new URLSearchParams(search)
  if (!params.has('state') || (!params.has('code') && !params.has('error'))) {
    return false
  }
  const pending = parse<PendingOAuth>(sessionStorage.getItem(PENDING_KEY))
  if (!pending || !isConnectorHash(pending.returnHash)) return false

  // cm-coding: Box must redirect to the registered document URL because OAuth
  // fragments are forbidden, but DocReview routes Vault inside the fragment.
  // Cache the short-lived callback before CAS mounts: an expired DocReview
  // session performs another redirect and its library intentionally drops the
  // query string. Restoring the Vault hash and retaining the state-bound query
  // lets the Vault exchange the code once without another Box login.
  sessionStorage.setItem(PENDING_KEY, JSON.stringify({
    ...pending,
    callbackSearch: search,
  } satisfies PendingOAuth))
  window.history.replaceState(
    {},
    '',
    `${window.location.pathname}${search}${pending.returnHash}`,
  )
  return true
}

export const consumeBoxOAuthCallback = (
  search = window.location.search,
  now = Date.now(),
): BoxOAuthCallback | null => {
  const pending = parse<PendingOAuth>(sessionStorage.getItem(PENDING_KEY))
  const currentParams = new URLSearchParams(search)
  const hasCurrentCallback = currentParams.has('code') || currentParams.has('state')
  const params = hasCurrentCallback
    ? currentParams
    : new URLSearchParams(pending?.callbackSearch || '')
  const code = params.get('code')
  const state = params.get('state')
  if (!code && !state) return null
  sessionStorage.removeItem(PENDING_KEY)
  if (
    !code
    || !state
    || !pending
    || !pending.redirectUri
    || !isConnectorHash(pending.returnHash)
    || pending.state !== state
    || now - pending.createdAt > MAX_PENDING_AGE_MS
  ) throw new Error('Box authorization could not be verified. Please connect again.')
  return { ...pending, code }
}

export const clearBoxOAuthQuery = () => {
  const url = new URL(window.location.href)
  url.searchParams.delete('code')
  url.searchParams.delete('state')
  url.searchParams.delete('error')
  url.searchParams.delete('error_description')
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
}
